import React from 'react';
import { Helmet } from 'react-helmet-async';
import './AnalyticsCup.scss';

export const AnalyticsCupRules = () => {
    return (
        <div className="analytics-cup">
            <Helmet>
                <title>Competition Rules — Analytics Cup | PySport</title>
            </Helmet>
            {/* Analytics Cup Header & Navigation */}
            <header className="analytics-header">
                <div className="container">
                    <div className="header-content">
                        <div className="brand-section">
                            <div className="brand-text">
                                <span className="brand-name">SkillCorner X PySport</span>
                                <span className="event-name">Analytics Cup</span>
                            </div>
                        </div>
                        <nav className="analytics-nav">
                            <div className="nav-links">
                                <a href="/analytics-cup" className="nav-link">Back to Analytics Cup</a>
                            </div>
                        </nav>
                    </div>
                </div>
            </header>

            {/* Rules Content */}
            <section className="rules-content">
                <div className="container py-5">
                    <div className="row justify-content-center">
                        <div className="col-lg-10">
                            <div className="rules-header text-center mb-5">
                                <h1 className="page-title">SkillCorner X PySport Analytics Cup Rules</h1>
                                <p className="page-subtitle">Official Competition Rules and Guidelines</p>
                            </div>

                            <div className="rules-sections">
                                <div className="rule-section mb-5">
                                    <h2>1. Competition Overview</h2>
                                    <div className="rule-content">
                                        <p>The <b>SkillCorner X PySport Analytics Cup</b> is a global hackathon-style challenge that brings together the sports analytics community to innovate with cutting-edge tracking data.</p>
                                        <ul>
                                            <li>Competition runs from October 2025 to February 2026</li>
                                            <li>Individual participants only (no team submissions)</li>
                                        </ul>
                                    </div>
                                </div>

                                <div className="rule-section mb-5">
                                    <h2>2. Eligibility</h2>
                                    <div className="rule-content">
                                        <ul>
                                            <li><strong>Age Requirement:</strong> Participants must be 18 years or older</li>
                                            <li><strong>Individual Only:</strong> No team submissions allowed</li>
                                            <li><strong>Global Participation:</strong> Open worldwide to individual participants</li>
                                            <li><strong>Employee Restriction:</strong> Employees of SkillCorner or PySport may participate but are not eligible for prizes, or presenting at the final event</li>
                                            <li><strong>Registration:</strong> Must complete official registration form and provide accurate information</li>
                                        </ul>
                                    </div>
                                </div>

                                <div className="rule-section mb-5">
                                    <h2>3. Competition Timeline</h2>
                                    <div className="rule-content">
                                        <ul>
                                            <li><strong>October 2025:</strong> Kick-Off </li>
                                            <li><strong>December 28th 2025:</strong> Submission Deadline</li>
                                            <li><strong>January 8th 2026:</strong> Results Announced</li>
                                            <li><strong>February 5th 2026:</strong> Paris Finale Invite-Only Event at SkillCorner HQ</li>
                                        </ul>
                                        <p className="rule-note">All dates are subject to change. Participants will be notified of any schedule updates.</p>
                                    </div>
                                </div>

                                <div className="rule-section mb-5">
                                    <h2>4. Submission Requirements</h2>
                                    <div className="rule-content">
                                        <ul>
                                            <li>Follow the submission template(s)
                                                <ul>
                                                    <li><strong><a href="https://github.com/PySport/analytics_cup_analyst">Analyst Track Template</a></strong></li>
                                                    <li><strong><a href="https://github.com/PySport/analytics_cup_research">Research Track Template</a></strong></li>
                                                </ul>
                                            </li>
                                            <li>Submit your final project <a href="https://pretalx.pysporg.org"><strong>here</strong></a></li>
                                            <li>All code must be original work by the participant</li>
                                            <li>One submission per participant per track</li>
                                            <li>No team submissions, individual participants only</li>
                                            <li>Open-source license required (MIT, Apache 2.0, or similar)</li>
                                            <li>Submissions should be created with open-source tools only (e.g. no Excel, Tableau, PowerBI etc.)</li>
                                            <li>Must demonstrate use of provided SkillCorner data</li>
                                        </ul>
                                    </div>
                                </div>

                                <div className="rule-section mb-5">
                                    <h2>5. Judging Criteria</h2>
                                    <div className="rule-content">
                                        <p>Submissions will be evaluated based on:</p>
                                        <ul>
                                            <li><strong>Innovation (25%):</strong> Novel approaches to sports analytics problems</li>
                                            <li><strong>Technical Quality (25%):</strong> Code quality, architecture, and documentation</li>
                                            <li><strong>Practical Impact (25%):</strong> Real-world applicability and value to the sports community</li>
                                            <li><strong>Open Source Potential (25%):</strong> Likelihood of becoming a valuable open-source tool</li>
                                        </ul>
                                    </div>
                                </div>

                                <div className="rule-section mb-5">
                                    <h2>6. Prizes and Awards</h2>
                                    <div className="rule-content">
                                        <ul>
                                            <li><strong>Top 6 Participants:</strong> €500 gift card each</li>
                                            <li><strong>Winner:</strong> €1,500 Open Source Grant to convert project into a Python package or pull request</li>
                                            <li><strong>Paris Finale:</strong> Selected finalists invited to give a 20-minute presentation at SkillCorner HQ in Paris on February 5th 2026</li>
                                        </ul>
                                        <p className="rule-note">Grant is released upon successful completion of the open-source deliverable.</p>
                                    </div>
                                </div>

                                <div className="rule-section mb-5">
                                    <h2>7. Code of Conduct</h2>
                                    <div className="rule-content">
                                        <p>All participants are expected to maintain professional standards throughout the competition:</p>
                                        <ul>
                                            <li>Respectful and inclusive behavior</li>
                                            <li>No harassment, discrimination, or inappropriate conduct</li>
                                            <li>Constructive feedback and collaboration</li>
                                            <li>Professional communication in all channels</li>
                                        </ul>
                                    </div>
                                </div>

                                <div className="rule-section mb-5">
                                    <h2>8. Intellectual Property</h2>
                                    <div className="rule-content">
                                        <ul>
                                            <li>Participants retain ownership of their original contributions</li>
                                            <li>All submissions must use open-source licenses</li>
                                            <li>SkillCorner retains rights to provided datasets and may use submissions for promotional purposes</li>
                                            <li>By participating, you grant permission to feature your work in case studies and presentations</li>
                                        </ul>
                                    </div>
                                </div>

                                <div className="rule-section mb-5">
                                    <h2>9. Disqualification</h2>
                                    <div className="rule-content">
                                        <p>Participants may be disqualified for:</p>
                                        <ul>
                                            <li>Violating these rules or the Code of Conduct</li>
                                            <li>Submitting plagiarized or non-original work</li>
                                            <li>Providing false information during registration</li>
                                            <li>Attempting to manipulate the judging process</li>
                                            <li>Late submission after the deadline</li>
                                            <li>Submitting work that does not execute / run out of the box</li>
                                        </ul>
                                    </div>
                                </div>

                                <div className="rule-section mb-5">
                                    <h2>10. Contact and Support</h2>
                                    <div className="rule-content">
                                        <p>For questions about these rules or the competition:</p>
                                        <p><strong>Discord:</strong> Sign up to receive a link to join our official Analytics Cup Discord server</p>
                                        <p>Rule clarifications and updates will be posted on the Discord server and website.</p>
                                    </div>
                                </div>
                            </div>

                            <div className="rules-footer text-center mt-5 pt-4 border-top">
                                <p><strong>Last Updated:</strong> October 10th 2025</p>
                                <p>These rules are subject to change. Participants will be notified of any updates.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};